import { useTranslation } from "react-i18next"

import { ArrowLeft, Maximize2, Minimize2, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { CitationViewerContent } from "@/features/chat/components/citation-viewer-content"
import { DownloadButton } from "@/features/file-viewer/components/download-button"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

// ── Types ─────────────────────────────────────────────────────────────────────

type TenderCitationPanelProps = {
  title: string
  documentUrl: string
  quoteSnippet?: string
}

// ── Component ─────────────────────────────────────────────────────────────────

export function TenderCitationPanel({ documentUrl, quoteSnippet = "" }: TenderCitationPanelProps) {
  return (
    <CitationViewerContent chunkSnippet="" documentUrl={documentUrl} quoteSnippet={quoteSnippet} />
  )
}

// ── Header (used by pushPanel renderHeader) ───────────────────────────────────

type TenderCitationHeaderProps = {
  title: string
  documentUrl: string
  closeable: boolean
  isFocusable: boolean
  isFocused: boolean
  onBack: () => void
  onClose: () => void
  onToggleFocus: () => void
}

function TenderCitationHeader({
  title,
  documentUrl,
  closeable,
  isFocusable,
  isFocused,
  onBack,
  onClose,
  onToggleFocus,
}: TenderCitationHeaderProps) {
  const { t } = useTranslation()
  return (
    <div className="flex shrink-0 flex-col border-b">
      <div className="border-b px-1 py-1">
        <Button className="h-8 gap-1.5 px-2" size="sm" variant="ghost" onClick={onBack}>
          <ArrowLeft className="size-4" />
          <span className="text-sm">{t("tender.citationPanel.backToChat")}</span>
        </Button>
      </div>
      <div className="flex items-center justify-between p-2">
        <h3 className="mr-2 flex-1 truncate text-sm font-semibold">{title}</h3>
        <div className="flex items-center gap-1">
          <DownloadButton fileUrl={documentUrl} title={title} />
          {isFocusable && (
            <Button
              className="h-8 w-8 shrink-0 p-0"
              size="icon"
              variant="ghost"
              aria-label={
                isFocused
                  ? t("tender.citationPanel.exitFocusMode")
                  : t("tender.citationPanel.focusMode")
              }
              onClick={onToggleFocus}
            >
              {isFocused ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            </Button>
          )}
          {closeable && (
            <Button
              aria-label={t("tender.citationPanel.close")}
              className="h-8 w-8 shrink-0 p-0"
              size="icon"
              variant="ghost"
              onClick={onClose}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

TenderCitationPanel.Header = TenderCitationHeader

// ── Helper: push the citation panel onto the content sidebar ──────────────────

// eslint-disable-next-line react-refresh/only-export-components
export function pushTenderCitationPanel({
  title,
  documentUrl,
  quoteSnippet = "",
}: {
  title: string
  documentUrl: string
  quoteSnippet?: string
}) {
  useContentSidebarStore.getState().pushPanel({
    id: "tender-citation-preview",
    title,
    headerMode: "custom",
    renderHeader: (context) => (
      <TenderCitationHeader
        closeable={context.closeable}
        documentUrl={documentUrl}
        isFocusable={context.isFocusable}
        isFocused={context.isFocused}
        title={title}
        onBack={() => useContentSidebarStore.getState().popPanel()}
        onClose={context.onClose}
        onToggleFocus={context.onToggleFocus}
      />
    ),
    content: (
      <TenderCitationPanel documentUrl={documentUrl} quoteSnippet={quoteSnippet} title={title} />
    ),
    focusable: true,
    closeable: true,
    resizable: true,
    isDirty: () => false,
  })
}
